/**
 * Reloads the app onto the live build when a newer one has been deployed.
 *
 * Chrome can bring back an old copy of the app (from its back/forward cache,
 * or a tab left open across a deploy) that keeps running against the current
 * API with nothing to tell the engineer. So we compare the build this bundle
 * came from with /version.json on start-up, when the page is restored from
 * the back/forward cache, and when the tab comes back to the foreground, and
 * reload silently if they differ. The basket lives server-side, so a reload
 * costs nothing, except on checkout: the job number and reason are typed into
 * the page there, so the reload waits until the engineer leaves checkout.
 *
 * If the check fails (offline, slow, unexpected response) the app carries on.
 */
import { useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'

import { APP_BUILD } from '../lib/build'
import { getAppEnv } from '../lib/env'

const VERSION_URL = '/version.json'

// The tab must be on the live build within 5 s of coming back, so give up on
// a slow check well before that.
const VERSION_CHECK_TIMEOUT_MS = 4_000

// The live build this tab last reloaded for. If a reload doesn't fix the
// mismatch (e.g. it landed mid-deploy), we don't reload for that build again,
// so the app can't get stuck reloading.
const RELOADED_FOR_BUILD_KEY = 'app_build_reloaded_for'

// /:tenantSlug/engineer/checkout (see components/layout/EngineerRoutes.tsx).
const CHECKOUT_PATH = /^\/[^/]+\/engineer\/checkout\/?$/

interface VersionFile {
  build?: unknown
}

async function fetchLiveBuild(signal: AbortSignal): Promise<string | null> {
  try {
    const response = await fetch(VERSION_URL, { cache: 'no-store', signal })
    if (!response.ok) {
      return null
    }
    const { build } = (await response.json()) as VersionFile
    return typeof build === 'string' && build ? build : null
  } catch {
    return null
  }
}

function reloadOnto(liveBuild: string): void {
  try {
    if (sessionStorage.getItem(RELOADED_FOR_BUILD_KEY) === liveBuild) {
      return
    }
    sessionStorage.setItem(RELOADED_FOR_BUILD_KEY, liveBuild)
  } catch {
    // Without storage we can't rule out a reload loop, so don't reload.
    return
  }
  window.location.reload()
}

export function useBuildVersionCheck(): void {
  const { pathname } = useLocation()
  const pathnameRef = useRef(pathname)
  // A newer build seen while on checkout, waiting for the engineer to leave.
  const heldBuildRef = useRef<string | null>(null)

  useEffect(() => {
    pathnameRef.current = pathname
    if (heldBuildRef.current && !CHECKOUT_PATH.test(pathname)) {
      reloadOnto(heldBuildRef.current)
    }
  }, [pathname])

  useEffect(() => {
    // `vite dev` serves no /version.json.
    if (getAppEnv() === 'local') {
      return
    }

    let inFlight: AbortController | null = null

    const check = async () => {
      if (inFlight || heldBuildRef.current) {
        return
      }
      const controller = new AbortController()
      inFlight = controller
      const timeout = window.setTimeout(() => controller.abort(), VERSION_CHECK_TIMEOUT_MS)
      const liveBuild = await fetchLiveBuild(controller.signal)
      window.clearTimeout(timeout)
      inFlight = null

      if (!liveBuild || liveBuild === APP_BUILD) {
        return
      }
      if (CHECKOUT_PATH.test(pathnameRef.current)) {
        heldBuildRef.current = liveBuild
        return
      }
      reloadOnto(liveBuild)
    }

    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        void check()
      }
    }
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        void check()
      }
    }

    void check()
    window.addEventListener('pageshow', handlePageShow)
    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => {
      inFlight?.abort()
      window.removeEventListener('pageshow', handlePageShow)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [])
}
